// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import siteMessage from "@insite/client-framework/SiteMessage";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import * as React from "react";
import { useContext } from "react";

export interface OrderDetailsTitleStyles {
    titleText: TypographyProps;
    // XNG-Changes
    creditHoldWarning?: TypographyPresentationProps;
}

export const titleStyles: OrderDetailsTitleStyles = {
    titleText: {
        variant: "h2",
        as: "h1",
    },
};

const styles = titleStyles;

const OrderDetailsTitle: React.FC = () => {
    const { value: order } = useContext(OrderStateContext);
    if (!order) {
        return null;
    }

    // XNG-Changes: TPS-938 Hide Opti Web Order Number
    const getOrderNumberDisplay = () => {
        if (!order.erpOrderNumber) {
            return siteMessage("Custom_WebOrder_Pending");
        }

        // XNG-Changes: TPS-1152
        const creditHold = order.properties?.creditHold?.toUpperCase();
        if (order.erpOrderNumber.toUpperCase().startsWith("E") || creditHold === "ERROR") {
            return siteMessage("Custom_OrderNumber_Pending");
        }

        return order.erpOrderNumber;
    };

    const showCreditHoldWarning = order.properties?.creditHold?.toUpperCase() === "FAIL";

    return (
        <>
            <Typography {...styles.titleText} data-test-selector="orderDetails_orderNumber">
                {`${translate("Order #")}${getOrderNumberDisplay()}`}
            </Typography>
            {showCreditHoldWarning && (
                <Typography {...styles.creditHoldWarning} data-test-selector="orderDetails_creditHoldWarning">
                    {siteMessage("Custom_Credit_Hold_Warning")}
                </Typography>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: OrderDetailsTitle,
    definition: {
        allowedContexts: ["OrderDetailsPage", "VmiOrderDetailsPage"],
        group: "Order Details",
    },
};

export default widgetModule;
